import { pluralize } from "../text";

export interface FilterItem<T = number | string> {
  id: T;
  name: string;
}

export const generateFilterLabel = <T extends string | number>(
  includedIds: T[],
  excludedIds: T[],
  items: FilterItem<T>[],
  itemType = ""
): string => {
  let includedPart = "";
  let excludedPart = "";

  if (includedIds.length === 1) {
    const item = items.find((item) => item.id === includedIds[0]);
    if (item) {
      includedPart =
        excludedIds.length > 0 ? `${item.name} included` : item.name;
    }
  } else if (includedIds.length > 1) {
    const itemText = itemType ? pluralize(itemType, includedIds.length) : "";
    const itemSuffix = itemText ? ` ${itemText}` : "";
    includedPart =
      excludedIds.length > 0
        ? `${includedIds.length}${itemSuffix} included`
        : `${includedIds.length}${itemSuffix}`;
  }

  if (excludedIds.length === 1) {
    const item = items.find((item) => item.id === excludedIds[0]);
    if (item) {
      excludedPart = `${item.name} excluded`;
    }
  } else if (excludedIds.length > 1) {
    const itemText = itemType ? pluralize(itemType, excludedIds.length) : "";
    const itemSuffix = itemText ? ` ${itemText}` : "";
    excludedPart = `${excludedIds.length}${itemSuffix} excluded`;
  }

  const parts = [includedPart, excludedPart].filter(Boolean);

  return parts.length > 0 ? parts.join(", ") : "";
};

/**
 * Generic tree item interface for hierarchical data
 */
export interface TreeItem {
  id: string;
  name: string;
  children?: TreeItem[];
}

/**
 * Transforms flat hierarchical data into a tree structure
 * @param data Array of items with id and parentId
 * @param nameFormatter Function to extract/format the name from each item
 * @param rootParentId The parentId value that indicates root items (default: "0")
 * @returns Array of root tree items with nested children
 */
export const transformToTree = <T extends { id: string; parentId: string }>(
  data: T[],
  nameFormatter: (item: T) => string,
  rootParentId: string = "0"
): TreeItem[] => {
  const itemsMap = new Map<string, TreeItem>();

  // First pass: create all items
  for (const item of data) {
    itemsMap.set(item.id, {
      id: item.id,
      name: nameFormatter(item),
      children: [],
    });
  }

  // Second pass: build the tree structure
  const rootItems: TreeItem[] = [];

  for (const item of data) {
    const treeItem = itemsMap.get(item.id);
    if (!treeItem) continue;

    if (item.parentId === rootParentId) {
      rootItems.push(treeItem);
    } else {
      const parent = itemsMap.get(item.parentId);
      if (parent) {
        parent.children = parent.children || [];
        parent.children.push(treeItem);
      }
    }
  }

  return rootItems;
};
